import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { FolderOpen, FileText, ChevronDown, ChevronRight, Download, Building2, Calendar, Wallet } from "lucide-react";

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const meseLabel = (m) => {
  if (!m) return "Tutti i mesi";
  const [y, mm] = m.split("-");
  const nomi = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  return `${nomi[parseInt(mm, 10) - 1]} ${y}`;
};
const fmtData = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };

export default function Documenti() {
  const [data, setData] = useState({ fornitori: [], mesi: [], totale_ivato: 0, totale_imponibile: 0, totale_iva: 0 });
  const [mese, setMese] = useState("");
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/documenti", { params: m ? { mese: m } : {} });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(mese); }, [mese]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">Documenti</h1>
      <p className="text-slate-400 mb-6">Archivio bolle in cartelle per fornitore. A fine mese vedi quanto pagare e incroci con la fattura ricevuta.</p>

      {/* Filtro mese + totale */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-md px-3 py-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select data-testid="documenti-mese" value={mese} onChange={(e) => setMese(e.target.value)} className="bg-transparent text-sm text-slate-100 outline-none">
            <option value="">Tutti i mesi</option>
            {data.mesi.map((m) => <option key={m} value={m} className="bg-slate-900">{meseLabel(m)}</option>)}
          </select>
        </div>
        <div data-testid="documenti-totale" className="flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-md px-4 py-2">
          <Wallet className="w-5 h-5 text-primary" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Totale da pagare ({meseLabel(mese)})</div>
            <div className="font-display font-black text-xl text-primary">{eur(data.totale_ivato)}</div>
          </div>
          <div className="text-xs text-slate-500 border-l border-slate-700 pl-3">
            Imponibile {eur(data.totale_imponibile)}<br />IVA {eur(data.totale_iva)}
          </div>
        </div>
      </div>

      {loading && <p className="text-slate-500 text-sm">Caricamento…</p>}
      {!loading && data.fornitori.length === 0 && <p className="text-slate-500 text-sm">Nessun documento per il periodo selezionato.</p>}

      <div className="space-y-3" data-testid="documenti-list">
        {data.fornitori.map((f) => {
          const fid = f.fornitore_id || "_senza";
          const isOpen = !!open[fid];
          return (
            <div key={fid} data-testid={`fornitore-folder-${fid}`} className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
              <button
                data-testid={`folder-toggle-${fid}`}
                onClick={() => toggle(fid)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-800/50 transition-colors text-left"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                <div className="w-10 h-10 rounded-md bg-amber-500/15 flex items-center justify-center shrink-0">
                  <FolderOpen className="w-5 h-5 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-100 flex items-center gap-1.5 truncate"><Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />{f.fornitore_nome}</div>
                  <div className="text-xs text-slate-500">{f.count} document{f.count === 1 ? "o" : "i"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-lg text-emerald-400">{eur(f.totale_ivato)}</div>
                  <div className="text-[11px] text-slate-500">imp. {eur(f.imponibile)} · IVA {eur(f.totale_iva)}</div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-800 divide-y divide-slate-800/60" data-testid={`folder-docs-${fid}`}>
                  {f.documenti.map((d) => (
                    <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 px-4 py-3 pl-16">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">Bolla n° <span className="font-mono-plex">{d.numero || "—"}</span></div>
                        <div className="text-xs text-slate-500">{fmtData(d.data)}</div>
                      </div>
                      <div className="text-right shrink-0 mr-2">
                        <div className="text-sm font-semibold text-slate-100">{eur(d.totale_ivato)}</div>
                        <div className="text-[11px] text-slate-500">imp. {eur(d.imponibile)}</div>
                      </div>
                      {d.pdf_file_id ? (
                        <a data-testid={`doc-pdf-${d.id}`} href={fileUrl(d.pdf_file_id)} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md px-2.5 py-1.5 transition-colors shrink-0">
                          <Download className="w-3.5 h-3.5" /> PDF
                        </a>
                      ) : (
                        <span className="text-[11px] text-slate-600 shrink-0">no PDF</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
